import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { usePlacesWidget } from "react-google-autocomplete";
import { Control, Controller } from "react-hook-form";
import { Form, Grid, SemanticWIDTHS, Label } from "semantic-ui-react";
import AppSettings from "../../../config";
import { parseGoogleAddress } from "../../../utils/ParseGoogleAddress";
import { makeFullAddress } from "../../../utils/functions";
import { countryPostalConnector, iso3ToIso2, mexicoStates3To2 } from "../../../constants/CountryCodesMap";
import useArcgisWidget from "../../../features/mapping/hooks/useArcGisWidget";
import useCountries from "../../../hooks/useCountries";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import PlaceholderUntilLoaded from "../PlaceholderUntilLoaded";
import { Country, formatAddressFields } from "./formatAddress";
import { useMedia } from "react-media";
import { AddressFormField } from "./address_form_fields/AddressFormField";
import { AttnFormField } from "./address_form_fields/AttnFormField";
import { SecondaryAddressFormField } from "./address_form_fields/SecondaryAddressFormField";
import { CountryFormField } from "./address_form_fields/CountryFormField";
import { CityFormField } from "./address_form_fields/CityFormField";
import { StateFormField } from "./address_form_fields/StateFormField";
import { ZipFormField } from "./address_form_fields/ZipFormField";
import "./ControlledAutofilledAddress.scss";
import useLocationsWidget from "../../../features/locations/hooks/useLocationsWidget";

interface IControlledAutofilledAddress {
  control: Control;
  name: string;
  coordName?: string;
  autofillCountry?: boolean;
  defaultValue?:
    | {
        address?: string;
        address2?: string;
        city?: string;
        state?: string;
        zip?: string;
        countryId?: string;
      }
    | { [key: string]: any };
  disabled?: boolean;
  required?: boolean;
  readonly?: boolean;
  errors?: any;
  validate?: {
    [name: string]: (value: {
      attn: string | null;
      address: string | null;
      address2: string | null;
      city: string | null;
      state: string | null;
      zip: string | null;
      countryId: string | null;
    }) => string | undefined;
  };

  autofill?: boolean;

  showAttnField?: boolean;
  showAttnFieldToggle?: boolean;
  showCountryField?: boolean;

  customAttnLabel?: string;
  customAddressLabel?: string;
  customAddress2Label?: string;
  customCityLabel?: string;
  customStateLabel?: string;
  customZipLabel?: string;

  customAttnSubLabel?: string;
  customAddressSubLabel?: string;
  customAddress2SubLabel?: string;
  customCitySubLabel?: string;
  customStateSubLabel?: string;
  customZipSubLabel?: string;

  customCountryLabel?: string;
  customCountrySubLabel?: string;

  valueAccessor?: string; // Used to access the address object if the control is a nested object (e.g. used in locations custom fields to reach "valueAddress")

  transform?: {
    input: (value: any) => any;
    output: (value: any) => any;
  };

  customLayout?: Array<Array<{ field: string; width: number; class?: string }>>;
  customGridClass?: string;
  labelClassName?: string;
}

type AutofilledAddressLookup =
  | {
      lookupMode?: "google" | "arcgis";
      onSelectPlace?: (
        place?: {
          address?: string;
          address2?: string;
          city?: string;
          state?: string;
          zip?: string;
          countryId?: string;
        } | null,
      ) => void;
    }
  | {
      lookupMode?: "locations";
      onSelectPlace?: (
        place?: {
          address?: string;
          address2?: string;
          city?: string;
          state?: string;
          zip?: string;
          countryId?: string;
          apn?: string;
          attributes?: Record<string, any>;
        } | null,
      ) => void;
    };

export default function ControlledAutofilledAddress({
  control,
  name,
  coordName,
  autofillCountry = true,
  defaultValue,
  required = false,
  disabled = false,
  readonly,
  errors,
  validate,

  autofill = true,

  showAttnField: showAttnFieldProp = false,
  showAttnFieldToggle = false,
  showCountryField = false,

  customAttnLabel,
  customAddressLabel,
  customAddress2Label,
  customCityLabel,
  customStateLabel,
  customZipLabel,

  customAttnSubLabel,
  customAddressSubLabel,
  customAddress2SubLabel,
  customCitySubLabel,
  customStateSubLabel,
  customZipSubLabel,

  valueAccessor,

  lookupMode = "google",
  onSelectPlace,

  transform = {
    input: (value) => {
      // Ensure all fields are always represented
      if (!value)
        return {
          attn: null,
          address: null,
          address2: null,
          city: null,
          state: null,
          zip: null,
          countryId: null,
        };
      return value;
    },
    output: (value) => {
      // Return null if all fields are empty
      const addressEmpty = Object.entries(value).every(([key, value]) => {
        if (key === "id") return true;
        return value === "" || value === null;
      });
      if (addressEmpty) return null;
      return value;
    },
  },
  customLayout,
  customGridClass,
  labelClassName,
}: IControlledAutofilledAddress & AutofilledAddressLookup) {
  const { isEnabled: useArcgis } = useFeatureFlag("global.esri_autocomplete");
  const isSmallScreen = useMedia({ query: "(max-width: 520px)" });
  const { setValue, getValues, trigger } = control;
  const errorsRef = useRef(errors); // We need to keep a reference to the errors object to access it in the Google Places API callback

  const effectiveLookupMode = lookupMode === "google" && useArcgis ? "arcgis" : lookupMode;
  const formFieldError = errors?.[name];

  const { countries, agencyCountry, countriesLoading, agencyCountryLoading } = useCountries({});

  // Sets the default value of the country flag once the agency country is loaded
  const onCountryLoaded = () => {
    const fieldValues = getValues(name);
    if (!autofillCountry || !showCountryField || !agencyCountry) return;
    const currentValues = fieldValues ?? defaultValue ?? {}; // Retrieve current (or default) values

    if (!!valueAccessor && valueAccessor in currentValues) {
      if (currentValues?.[valueAccessor]?.countryId) return; // If countryId is already set, do nothing
      const currentValueAddress = currentValues[valueAccessor];
      setValue(name, {
        ...currentValues,
        valueAddress: { ...currentValueAddress, countryId: agencyCountry.id },
      });
    } else {
      if (currentValues.countryId) return; // If countryId is already set, do nothing
      setValue(name, { ...currentValues, countryId: agencyCountry.id });
    }
  };

  const [showAttnField, setShowAttnField] = useState<boolean>(
    !!transform.input(defaultValue)?.attn || showAttnFieldProp,
  );

  // state to track if google places api is ready; we want to update this when the script loads to force a remount
  const [googleReady, setGoogleReady] = useState(!!window.google?.maps?.places);
  useEffect(() => {
    if (googleReady) return;

    const checkInterval = setInterval(() => {
      if (window.google?.maps?.places) {
        setGoogleReady(true);
        clearInterval(checkInterval);
      }
    }, 100);

    setTimeout(() => clearInterval(checkInterval), 10000);

    return () => clearInterval(checkInterval);
  }, [googleReady]);

  /**
   * Keep errors reference up to date
   */
  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  /**
   * Load Google Places API script if not already loaded for staff
   */
  useEffect(() => {
    if (window.google?.maps?.places) return;
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${AppSettings.GOOGLE_GEOCODING_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  /**
   * Retrieves the country data for the current countryId. If no countryId is provided, the agency country is used. Otherwise the default is "US".
   *
   * @param countryId - The ISO2 country to retrieve data for
   */
  const getCountryData = (countryId?: string): Country => {
    const country = countryId || agencyCountry?.id || "US";
    return countries[country];
  };

  const anyFieldsFilledIn = (value: any) => {
    const transformedValue = transform.input(value);
    if (!transformedValue) return false;
    const { address, city, state, zip } = transformedValue;
    return address || city || state || zip;
  };

  const allFieldsFilledIn = (value: any, countryUsesState: boolean = true) => {
    const transformedValue = transform.input(value);
    if (!transformedValue) return false;
    const { address, city, state, zip } = transformedValue;
    return address && city && (state || !countryUsesState) && zip;
  };

  // Country data
  const controlVals = control.watchInternal();
  const controlAccessor = valueAccessor ? `${name}.${valueAccessor}` : name;
  const addressObj = _.get(controlVals, controlAccessor); // The address object from the control
  const countryId = addressObj?.countryId || agencyCountry?.id || "US";
  const countryData = getCountryData(countryId);

  // if city, state, or zip have a sub-label, push padding down for ALL of them so that the input fields align properly
  const cityStateZipHasSubLabel = !!(customCitySubLabel || customStateSubLabel || customZipSubLabel);

  // If address is set to read-only, display address as a string
  if (readonly) {
    const formattedAddress = makeFullAddress(
      defaultValue?.address,
      defaultValue?.address2,
      defaultValue?.city,
      defaultValue?.state,
      defaultValue?.zip,
      null,
    );
    return (
      <Grid.Row>
        <Grid.Column>
          <Form.Field>
            <label htmlFor='address'>{customAddressLabel || "Address"}</label>
            {formattedAddress ? (
              <span id='address'>{formattedAddress}</span>
            ) : (
              <span id='address' style={{ color: "gray" }}>
                N/A
              </span>
            )}
          </Form.Field>
        </Grid.Column>
      </Grid.Row>
    );
  }

  // Otherwise render normally
  return (
    <Controller
      key={`autocomplete-input-${effectiveLookupMode}-${googleReady ? "ready" : "loading"}`}
      control={control}
      name={name}
      defaultValue={defaultValue}
      rules={{
        validate: {
          addressRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.address) return "Address is required";
          },
          countryRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.countryId) return "Country is required";
          },
          cityRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.city) return "City is required";
          },
          stateRequired: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value; // Need to use valueAccessor to support nested objects
            const countryData = getCountryData(addressObj?.countryId);
            if (!required || !countryData.hasStateInAddress) return;
            if (!transform.input(value)?.state) return "State is required";
          },
          zipRequired: (value) => {
            if (!required) return;
            if (!transform.input(value)?.zip) return "Zip is required";
          },
          allFieldsIfAny: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value; // Need to use valueAccessor to support nested objects
            const countryData = getCountryData(addressObj?.countryId);
            if (allFieldsFilledIn(value, countryData.hasStateInAddress)) return;
            if (anyFieldsFilledIn(value)) {
              if (!countryData.hasStateInAddress) {
                return "Address, City, and Zip must have a value";
              } else {
                return "Address, City, State, and Zip must have a value";
              }
            }
          },
          // Validates that zip code matches regex for the selected country
          zipMatchesFormat: (value) => {
            if (!required && !anyFieldsFilledIn(value)) return;
            const addressObj = valueAccessor ? value[valueAccessor] : value;
            const countryData = getCountryData(addressObj?.countryId);

            const postalFormat = countryData?.postalFormat;
            if (postalFormat) {
              const regex = new RegExp(postalFormat, "g");
              if (!regex.test(addressObj?.zip)) return "Zip must match the postal format";
            }
          },
          // If the country has states and a state is provided, validate that the state is in the list of states
          validState: (value) => {
            const addressObj = valueAccessor ? value[valueAccessor] : value;
            const countryData = getCountryData(addressObj?.countryId);

            if (addressObj?.state && countryData?.states && !countryData.states.includes(addressObj.state)) {
              return `Invalid state value: '${addressObj.state}'`;
            }
          },
          custom: (value) => {
            const validationFunctions = _.values(validate);
            for (const validationFunction of validationFunctions) {
              const response = validationFunction(transform.input(value));
              if (response) return response;
            }
          },
        },
      }}
      render={({ onChange, value }) => {
        const { ref } = (() => {
          const arcgisRef = useArcgisWidget({
            onPlaceSelected: (place) => {
              if (!place.attributes.StAddr) return;
              const { StAddr, ShortLabel, City: city, RegionAbbr, Postal, PostalExt, Country } = place.attributes;
              const address = StAddr || ShortLabel || "";
              const countryId = iso3ToIso2[Country]?.toLocaleUpperCase() || "US";
              const zip = Postal + (PostalExt ? `${countryPostalConnector[countryId]}${PostalExt}` : "");
              // Validator uses the 2 letter mexico state codes, but esri returns 3 letter codes
              const state = countryId === "MX" ? mexicoStates3To2[RegionAbbr] || RegionAbbr : RegionAbbr;
              const coords = place.location;
              if (coordName && coords) {
                setValue(coordName, `${coords.x},${coords.y}`);
                trigger(coordName);
              }

              const value = {
                address,
                city,
                state,
                zip,
                countryId,
              };
              handleChange(value);
              onSelectPlace?.(value);

              // Bit of a hack to get it to revalidate after inserting new values
              if (Object.keys(errorsRef.current ?? {}).length > 0) trigger(name);
            },
          });

          const locationsRef = useLocationsWidget({
            onPlaceSelected: (place) => {
              if (coordName && place.location) {
                setValue(coordName, `${place.location.x},${place.location.y}`);
                trigger(coordName);
              }

              const value = {
                address: place.address,
                address2: place.address2 || "",
                city: place.city,
                state: place.state,
                zip: place.zip,
                countryId: place.countryId,
                apn: place.apn || "",
                attributes: place.attributes,
              };
              handleChange(value);
              onSelectPlace?.(value);

              if (Object.keys(errorsRef.current ?? {}).length > 0) {
                trigger(name);
              }
            },
            getCoords: !!coordName,
          });

          const googleRef = usePlacesWidget({
            apiKey: AppSettings.GOOGLE_GEOCODING_KEY,
            onPlaceSelected: (place) => {
              if (!place.address_components) return;
              const addressObj = parseGoogleAddress(place.address_components);
              const address = addressObj.full_street || place.name || "";
              const city = addressObj.city;
              const zip = addressObj.postal_code;
              const countryId = addressObj.country;
              const state = addressObj.region_short.length <= 3 ? addressObj.region_short : ""; // If state is provided non-abbreviated, don't use it

              const value = {
                address,
                city,
                state,
                zip,
                countryId,
              };
              handleChange(value);
              onSelectPlace?.(value);

              // Bit of a hack to get it to revalidate after inserting new values
              if (Object.keys(errorsRef.current).length > 0) {
                trigger(name);
              }
            },
            options: {
              types: [],
              fields: ["address_components", "name"],
            },
          });
          if (effectiveLookupMode === "arcgis") return arcgisRef;
          if (effectiveLookupMode === "locations") return locationsRef;
          return googleRef;
        })();

        const handleChange = (change: { [key: string]: any }) => {
          onChange(
            transform.output({
              ...transform.input(value),
              ...change,
            }),
          );
        };

        // Props that are shared across all address form fields
        const commonProps = {
          value,
          disabled,
          error: formFieldError,
          required,
          handleChange,
          labelClassName,
          transform,
          isMobileLayout: isSmallScreen,
        };

        // Map the address fields to their respective components
        const addressFieldsLookup: {
          [key: string]: JSX.Element;
        } = {
          attn: <AttnFormField {...commonProps} label={customAttnLabel} subLabel={customAttnSubLabel} />,
          address: (
            <AddressFormField
              {...commonProps}
              label={customAddressLabel}
              subLabel={customAddressSubLabel}
              showAttnField={showAttnField}
              showAttnFieldToggle={showAttnFieldToggle}
              handleChange={handleChange}
              autofill={autofill}
              autofillRef={ref}
              setShowAttnField={setShowAttnField}
            />
          ),
          address2: (
            <SecondaryAddressFormField
              {...commonProps}
              label={customAddress2Label}
              subLabel={customAddress2SubLabel}
              countryField={
                showCountryField ? (
                  <CountryFormField
                    {...commonProps}
                    onCountryLoaded={onCountryLoaded}
                    value={{ countryId }}
                    subLabelPadding={!!customAddress2SubLabel}
                  />
                ) : null
              }
            />
          ),
          city: (
            <CityFormField
              {...commonProps}
              label={customCityLabel}
              subLabel={customCitySubLabel}
              subLabelPadding={cityStateZipHasSubLabel}
            />
          ),
          state: (
            <StateFormField
              {...commonProps}
              label={customStateLabel}
              subLabel={customStateSubLabel}
              subLabelPadding={cityStateZipHasSubLabel}
              countryId={countryId}
            />
          ),
          zip: (
            <ZipFormField
              {...commonProps}
              label={customZipLabel}
              subLabel={customZipSubLabel}
              subLabelPadding={cityStateZipHasSubLabel}
            />
          ),
        };

        const addressFields =
          customLayout ||
          formatAddressFields({
            country: countryData ?? agencyCountry,
            showAttn: showAttnField || value?.attn,
            mobileView: isSmallScreen,
          });

        return (
          <PlaceholderUntilLoaded isLoading={countriesLoading || agencyCountryLoading} lineCount={5}>
            {/* Invisible controller for the coords */}
            {coordName && (
              <Controller
                name={coordName}
                control={control}
                defaultValue={null}
                render={(field) => <input {...field} type='hidden' />}
              />
            )}
            {/* Address fields */}
            <Grid className={`controlled-autofilled-address ${customGridClass}`}>
              {addressFields.map((columns, i) => {
                return (
                  <Grid.Row key={i}>
                    {columns.map((col, i) => {
                      return (
                        <Grid.Column key={i} width={col.width as SemanticWIDTHS} className={(col as any).class}>
                          {addressFieldsLookup[col.field]}
                        </Grid.Column>
                      );
                    })}
                  </Grid.Row>
                );
              })}
              {/* Error message */}
              {!!formFieldError && (
                <Grid.Row>
                  <Grid.Column>
                    <Label basic prompt pointing='above' aria-live='polite' color='red'>
                      {formFieldError.message}
                    </Label>
                  </Grid.Column>
                </Grid.Row>
              )}
            </Grid>
          </PlaceholderUntilLoaded>
        );
      }}
    />
  );
}
